import api from '@/utils/axios';
import * as mockData from '../mock/clientDashboard.mock';

// Passe à FALSE pour désactiver les mocks et brancher tes endpoints d'un coup
const USE_MOCK = true;

export interface UserProfile {
    id: number;
    firstnames: string;
    lastname: string;
    email: string | null;
    phone: string;
    status: string;
    zone?: { id: number; name: string };
}

export interface Order {
    id: number;
    code_suivi: string;
    statut: string;
    type_livraison?: string;
    adresse_ramassage: string;
    adresse_livraison: string;
    prix_estime?: number;
    distance_km?: number;
    created_at: string;
    livraisons?: Array<{
        id: number;
        statut: string;
        livreur?: { id: number; firstnames: string; lastname: string; phone: string };
    }>;
}

export interface Address {
    id: number;
    user_id: number;
    nom_adresse: string;
    adresse_complete: string;
    est_principale: boolean;
    point_repere?: string;
    quartier?: string;
    ville?: string;
    pays?: string;
    code_postal?: string;
}

export interface Notification {
    id: number;
    titre: string;
    message: string;
    est_lu: boolean;
    created_at: string;
}

// Profil utilisateur
export async function fetchUserProfile(): Promise<UserProfile> {
    if (USE_MOCK) return Promise.resolve(mockData.mockUserProfile);
    const res = await api.get('/me');
    return res.data.data ?? res.data;
}

export async function updateUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    if (USE_MOCK) return Promise.resolve({ ...mockData.mockUserProfile, ...data });
    const res = await api.patch('/users/me', data);
    return res.data.data ?? res.data;
}

// Commandes client
export async function fetchClientOrders(): Promise<Order[]> {
    if (USE_MOCK) return Promise.resolve(mockData.mockClientOrders);
    const res = await api.get('/orders/client');
    return res.data.data ?? res.data ?? [];
}

export async function fetchOrderDetail(orderId: number): Promise<Order> {
    if (USE_MOCK) {
        const found = mockData.mockClientOrders.find(o => o.id === orderId);
        return found ? Promise.resolve(found) : Promise.reject(new Error('Order not found'));
    }
    const res = await api.get(`/orders/${orderId}`);
    return res.data.data ?? res.data;
}

export async function createOrder(data: unknown): Promise<Order> {
    if (USE_MOCK) return Promise.resolve(mockData.mockClientOrders[0]);
    const res = await api.post('/orders', data);
    return res.data.data ?? res.data;
}

// Adresses
export async function fetchClientAddresses(): Promise<Address[]> {
    if (USE_MOCK) return Promise.resolve(mockData.mockClientAddresses);
    const res = await api.get('/addresses');
    return res.data.data ?? res.data ?? [];
}

export async function createAddress(data: Partial<Address>): Promise<Address> {
    if (USE_MOCK) return Promise.resolve({ ...mockData.mockClientAddresses[0], ...data } as Address);
    const res = await api.post('/addresses', data);
    return res.data.data ?? res.data;
}

export async function updateAddress(id: number, data: Partial<Address>): Promise<Address> {
    if (USE_MOCK) return Promise.resolve({ ...mockData.mockClientAddresses[0], ...data } as Address);
    const res = await api.patch(`/addresses/${id}`, data);
    return res.data.data ?? res.data;
}

export async function deleteAddress(id: number): Promise<void> {
    if (USE_MOCK) return Promise.resolve();
    await api.delete(`/addresses/${id}`);
}

// Notifications
export async function fetchNotifications(): Promise<Notification[]> {
    if (USE_MOCK) return Promise.resolve(mockData.mockNotifications);
    const res = await api.get('/notifications');
    return res.data.data ?? res.data ?? [];
}

export async function markNotificationAsRead(id: number): Promise<void> {
    if (USE_MOCK) return Promise.resolve();
    await api.patch(`/notifications/${id}/mark-as-read`);
}

// Photos de profil
export async function uploadProfilePhoto(file: File): Promise<{ photo_url: string }> {
    if (USE_MOCK) return Promise.resolve({ photo_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb' });
    const formData = new FormData();
    formData.append('photo', file);
    const res = await api.post('/users/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data ?? res.data;
}